import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useQueryView } from "../lib/loading/useQueryView";
import { PageError } from "./PageError";
import { PageSkeleton } from "./PageSkeleton";

// Sugar over useQueryView for a screen (or section) that waits on one
// query: the four states of the loading contract in one place, so a
// route can't hand-roll `isLoading || !data` again.
//
// - loading → the caller's skeleton inside PageSkeleton. Mounted from the
//   first render (hidden during the delay) so the body's space is
//   reserved; past SKELETON_SLOW_MS a notice with Reintentar appears
//   under it, without removing it.
// - error → PageError with Reintentar. Offline (the first fetch is
//   paused waiting for a connection) gets the «Sin conexión» copy, the
//   same words the PWA's ResourceView uses.
// - empty / disabled → `empty` (disabled renders nothing without it, but
//   never a skeleton: nothing is on its way).
// - ready → children, plus an inline notice if a refresh failed over the
//   kept data, and a quiet «Actualizando…» on a long background refresh.
//
// `kind="section"` is for a panel inside a page that already has its own
// loading frame: no status announcement and an error that never moves
// focus.
type BaseProps<T> = {
  query: UseQueryResult<T, unknown>;
  /** A noun phrase, never a sentence: "tus tiendas", "el reporte". The
   *  boundary builds «Cargando {label}…» (announced once the skeleton
   *  paints) and «No pudimos cargar {label}» (the error title) — same
   *  meaning as the PWA's ResourceView `label`. */
  label: string;
  skeleton: ReactNode;
  /** Overrides the built «No pudimos cargar {label}». */
  errorTitle?: string;
  waitingOn?: UseQueryResult<unknown, unknown>[];
  kind?: "page" | "section";
  children: (data: T) => ReactNode;
};

// Whoever says what "empty" means also says what it looks like.
type EmptyProps<T> =
  | { isEmpty: (data: T) => boolean; empty: ReactNode }
  | { isEmpty?: undefined; empty?: ReactNode };

export type QueryBoundaryProps<T> = BaseProps<T> & EmptyProps<T>;

export function QueryBoundary<T>(props: QueryBoundaryProps<T>) {
  const { query, label, skeleton, errorTitle, waitingOn, kind = "page", children } = props;
  const view = useQueryView(query, {
    ...(props.isEmpty && { isEmpty: props.isEmpty }),
    ...(waitingOn && { waitingOn }),
  });
  const retry = () => void query.refetch();

  switch (view.kind) {
    case "loading":
      return (
        <>
          <PageSkeleton
            label={`Cargando ${label}…`}
            visible={view.visible}
            announce={kind === "page"}
            kind={kind}
          >
            {skeleton}
          </PageSkeleton>
          {view.slow && (
            <div className="notice-banner notice-banner--action">
              <span>
                <strong>Esto está tardando más de lo normal.</strong> Revisa tu conexión. Seguimos
                intentando.
              </span>
              <button type="button" className="btn btn--secondary btn--sm" onClick={retry}>
                Reintentar
              </button>
            </div>
          )}
        </>
      );
    case "error":
      return view.offline ? (
        <PageError
          title="Sin conexión"
          description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
          onRetry={view.retry}
          autoFocus={kind === "page"}
        />
      ) : (
        <PageError
          title={errorTitle ?? `No pudimos cargar ${label}`}
          description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
          onRetry={view.retry}
          autoFocus={kind === "page"}
        />
      );
    case "empty":
    case "disabled":
      return <>{props.empty ?? null}</>;
    case "ready":
      return (
        <>
          {view.refreshFailed && (
            <div className="notice-banner notice-banner--action">
              <span>No pudimos actualizar. Mostrando lo último que tienes guardado.</span>
              <button type="button" className="btn btn--secondary btn--sm" onClick={retry}>
                Reintentar
              </button>
            </div>
          )}
          {view.revalidatingHint && <p className="refresh-hint">Actualizando…</p>}
          {children(view.data)}
        </>
      );
  }
}
